import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import axiosInstance from '../utils/router/axiosInstance';
import { logger } from '../utils/logger';

// WEB-2: сессия владельца для UI (username/role/isAdmin). Роль-гейт на фронте —
// UX (скрыть админ-действия), НЕ защита: сервер авторизует по роли из БД.
// Источник истины по РОЛИ — ТОЛЬКО GET /website/me (ревью Codex: клейм из JWT
// не должен открывать админ-UI ни до ответа /me, ни после его ошибки); из
// клейма берём лишь username для мгновенного отображения. Безопасный дефолт:
// роль не подтверждена -> НЕ админ, действия скрыты.

const AuthContext = createContext({
  username: null,
  role: null,
  id: null,
  messengerV2: false,
  msgInvites: false,
  msgRooms: false,
  isAdmin: false,
  loaded: false,
  refreshMe: async () => {},
});

/**
 * Публичные маршруты, где mount-вызов /me не нужен и вреден: '/' (логин) и
 * QR-1b-лендинг '/j/' (приглашённый БЕЗ сессии владельца; stale access_token
 * владельца в том же браузере -> 401 -> интерсептор -> refresh -> при мёртвой
 * cookie window.location.href='/' = лендинг улетает на форму логина, High-1
 * дизайн-ревью QR-1). Точные пути, не префикс: '/jobs' публичным не станет.
 */
export const PUBLIC_PATHS = ['/', '/j', '/j/', '/j/chat'];
export const isPublicPath = (pathname) => PUBLIC_PATHS.includes(pathname);

function usernameFromToken() {
  try {
    const token = localStorage.getItem('access_token');
    if (!token) return null;
    return jwtDecode(token).sub || null;
  } catch {
    return null; // битый/чужой токен — не роняем UI, истину даст /me
  }
}

export const AuthProvider = ({ children }) => {
  const [state, setState] = useState(() => ({
    username: usernameFromToken(),
    role: null, // роль ТОЛЬКО от сервера (/me)
    id: null, // MSG-7: id владельца (@o<id>) — тоже только от сервера
    messengerV2: false, // MSG-7: гейт страницы чата; дефолт «чата нет»
    msgInvites: false, // QR-1b: гейт «Мой QR»/чата с участником; дефолт «нет»
    msgRooms: false, // QR-2 В2: гейт ОБЩЕЙ комнаты; дефолт «комнаты нет»
    loaded: false,
  }));

  const refreshMe = useCallback(async () => {
    if (!localStorage.getItem('access_token')) {
      setState({
        username: null, role: null, id: null, messengerV2: false, msgInvites: false,
        msgRooms: false, loaded: true,
      });
      return;
    }
    try {
      const { data } = await axiosInstance.get('website/me');
      setState({
        username: data.username,
        role: data.role,
        id: data.id ?? null,
        // строгое === true: старый сервер без поля читается как «чата нет»
        messengerV2: data.messenger_v2 === true,
        // сервер отдаёт msg_invites = messenger_v2 && msg_invites_enabled
        msgInvites: data.msg_invites === true,
        // сервер отдаёт msg_rooms = messenger_v2 && msg_rooms_enabled
        msgRooms: data.msg_rooms === true,
        loaded: true,
      });
    } catch (error) {
      logger.error('Не удалось получить профиль /website/me', error);
      // Безопасный дефолт (ревью Codex): сервер роль НЕ подтвердил — роль
      // сбрасываем, админ-действия скрыты. Клейм из JWT — быстрый старт
      // ТОЛЬКО до ответа /me, не fallback-истина после ошибки.
      setState((prev) => ({
        username: prev.username,
        role: null,
        id: null,
        messengerV2: false,
        msgInvites: false,
        msgRooms: false,
        loaded: true,
      }));
    }
  }, []);

  useEffect(() => {
    // Публичный маршрут '/' (логин): mount-вызов /me не нужен и вреден
    // (ревью-воркфлоу: stale access_token -> 401 -> общий интерсептор -> при
    // мёртвом refresh-cookie window.location.href='/' = перезагрузка формы
    // логина под руками у юзера + лишний /me+/refresh трафик на публичной
    // странице). После логина auth.jsx зовёт refreshMe() явно; SPA-переходов
    // с '/' без логина нет (у формы нет ссылок), любой другой вход в
    // приложение — полная загрузка страницы с новым mount.
    // QR-1b: '/j/' — публичный лендинг приглашения, та же логика (isPublicPath).
    if (isPublicPath(window.location.pathname)) {
      setState((prev) => ({ ...prev, loaded: true }));
      return;
    }
    refreshMe();
  }, [refreshMe]);

  const value = useMemo(
    // loaded в isAdmin — ремень к подтяжкам: роль и так null до ответа /me
    () => ({ ...state, isAdmin: state.loaded && state.role === 'admin', refreshMe }),
    [state, refreshMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
